import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Cookie } from 'lucide-react';
import { Button } from './ui/button';

const COOKIE_CONSENT_KEY = 'cookie_consent';
const API_URL = process.env.REACT_APP_BACKEND_URL;

const loadMetaPixel = (pixelId) => {
  if (!pixelId || window.fbq) return;
  (function(f, b, e, v, n, t, s) {
    if (f.fbq) return;
    n = f.fbq = function() {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n; n.loaded = true; n.version = '2.0'; n.queue = [];
    t = b.createElement(e); t.async = true; t.src = v;
    s = b.getElementsByTagName(e)[0]; s.parentNode.insertBefore(t, s);
  })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
  window.fbq('init', pixelId);
  window.fbq('track', 'PageView');
};

const loadGoogleAnalytics = (gaId) => {
  if (!gaId || window.gtag) return;
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
  document.head.appendChild(script);
  window.dataLayer = window.dataLayer || [];
  window.gtag = function() { window.dataLayer.push(arguments); };
  window.gtag('js', new Date());
  window.gtag('config', gaId, { send_page_view: true, cookie_flags: 'SameSite=None;Secure' });
};

const loadTracking = (settings) => {
  if (settings.metaPixelId) loadMetaPixel(settings.metaPixelId);
  if (settings.googleAnalyticsId) loadGoogleAnalytics(settings.googleAnalyticsId);
};

const CookieBanner = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!consent) {
      const timer = setTimeout(() => setIsVisible(true), 1000);
      return () => clearTimeout(timer);
    }
    // If already accepted all, load tracking
    try {
      const parsed = JSON.parse(consent);
      if (parsed.marketing) {
        fetch(`${API_URL}/api/settings`)
          .then(r => r.json())
          .then(d => loadTracking(d))
          .catch(() => {});
      }
    } catch {}
  }, []);

  const acceptAll = () => {
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify({
      essential: true,
      analytics: true,
      marketing: true,
      timestamp: new Date().toISOString()
    }));
    setIsVisible(false);
    // Load all tracking immediately
    fetch(`${API_URL}/api/settings`)
      .then(r => r.json())
      .then(d => loadTracking(d))
      .catch(() => {});
  };

  const acceptEssential = () => {
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify({
      essential: true,
      analytics: false,
      marketing: false,
      timestamp: new Date().toISOString()
    }));
    setIsVisible(false);
  };

  if (!isVisible) return null;

  return (
    <div 
      className="fixed bottom-0 left-0 right-0 z-[100] p-4 animate-slide-up"
      data-testid="cookie-banner"
    >
      <div className="max-w-4xl mx-auto bg-[#0a1118] border border-[#50c878]/40 rounded-2xl shadow-2xl overflow-hidden">
        <div className="p-4 sm:p-6">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-full bg-[#50c878]/20 flex items-center justify-center flex-shrink-0">
              <Cookie className="w-5 h-5 text-[#50c878]" />
            </div>
            
            <div className="flex-1 min-w-0">
              <h3 className="text-[#f5f5dc] font-semibold text-lg mb-2">
                Usamos cookies
              </h3>
              <p className="text-[#f5f5dc]/60 text-sm leading-relaxed mb-4">
                Utilizamos cookies propias y de terceros para mejorar tu experiencia, 
                analizar el tráfico y mostrar publicidad personalizada. Puedes aceptar 
                todas las cookies o configurar tus preferencias.
              </p>
              
              {showDetails && (
                <div className="mb-4 p-4 bg-[#1a2b3c] rounded-lg space-y-3 text-sm">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-[#f5f5dc] font-medium">Cookies esenciales</span>
                      <p className="text-[#f5f5dc]/50 text-xs">Necesarias para el funcionamiento del sitio</p>
                    </div>
                    <span className="text-[#50c878] text-xs">Siempre activas</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-[#f5f5dc] font-medium">Cookies analíticas</span>
                      <p className="text-[#f5f5dc]/50 text-xs">Nos ayudan a entender cómo usas el sitio</p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-[#f5f5dc] font-medium">Cookies de marketing</span>
                      <p className="text-[#f5f5dc]/50 text-xs">Permiten mostrarte publicidad relevante</p>
                    </div>
                  </div>
                </div>
              )}
              
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3">
                <Button
                  onClick={acceptAll}
                  className="bg-[#50c878] text-white hover:bg-[#40a0a0] px-6 py-2.5 rounded-full text-sm font-medium"
                  data-testid="cookie-accept-all"
                >
                  Aceptar todas
                </Button>
                <Button
                  onClick={acceptEssential}
                  variant="outline"
                  className="border-[#f5f5dc]/30 text-[#f5f5dc] hover:bg-[#f5f5dc]/10 px-6 py-2.5 rounded-full text-sm"
                  data-testid="cookie-accept-essential"
                >
                  Solo esenciales
                </Button>
                <button
                  onClick={() => setShowDetails(!showDetails)}
                  className="text-[#50c878] hover:text-[#f5f5dc] text-sm underline underline-offset-2 py-2"
                  data-testid="cookie-show-details"
                >
                  {showDetails ? 'Ocultar detalles' : 'Ver detalles'}
                </button>
              </div>
              
              <p className="mt-3 text-[#f5f5dc]/40 text-xs">
                Más información en nuestra{' '}
                <Link to="/politica-cookies" className="text-[#50c878] hover:underline">
                  Política de Cookies
                </Link>
                {' '}y{' '}
                <Link to="/politica-privacidad" className="text-[#50c878] hover:underline">
                  Política de Privacidad
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieBanner;
